import React, { useState, useEffect } from 'react';
import './BookingFormModal.css';
import axios from 'axios';

function BookingFormModal({ slot, userId, onClose, refreshCalendar }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    notes: '',
    timezone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'success', 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [meetLink, setMeetLink] = useState(null);

  useEffect(() => {
    // Automatically detect the user's timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setFormData(prevData => ({ ...prevData, timezone }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Function to open modal with specific type and message
  const showModal = (type, message) => {
    setModalType(type);
    setModalMessage(message);
    setModalVisible(true);
  };

  // Function to close modal
  const closeModal = () => {
    setModalVisible(false);
    if (modalType === 'success') {
      refreshCalendar(); // Refresh calendar after successful booking
      onClose(); // Close the booking modal
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Set submitting state to true
    
    const bookingData = {
      ...formData,
      clientName: formData.name,  // Include the full name of the person booking
      clientEmail: formData.email,
      notes: formData.notes,
      startTime: slot.start.toISOString(),
      endTime: slot.end.toISOString(),
      userId,
    };

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/book-session`, bookingData);
      if (response.status === 200) {
        if (response.data.meetLink) {
          setMeetLink(response.data.meetLink);
        }
        showModal('success', 'Booking confirmed! Check your email for details.');
        setFormData({ name: '', email: '', notes: '', timezone: '' }); // Reset form
      } else {
        showModal('error', 'Failed to confirm the booking.');
      }
    } catch (error) {
      console.error('Failed to book session:', error);
      showModal('error', 'An error occurred while booking the session.');
    } finally {
      setIsSubmitting(false); // Set submitting state back to false
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {modalVisible ? (
          /* ── Success / Error state replaces the form inline ── */
          <div>
            <div className="modal-header">
              {modalType === 'success' ? '✅ Confirmed!' : '❌ Error'}
            </div>
            <div className="modal-body">
              {modalMessage}
            </div>
            <div className="modal-footer">
              <button onClick={closeModal} className="modal-close-btn">OK</button>
            </div>
          </div>
        ) : (
          /* ── Booking form ── */
          <>
            <h2 className="modal-title">Book a Session</h2>
            <p className="time-slot">Selected time slot: <strong>{slot.start.toLocaleString()} - {slot.end.toLocaleString()}</strong> (Timezone: {formData.timezone})</p>

            <form onSubmit={handleSubmit} className="booking-form">
              <div className="form-group">
                <label htmlFor="name">Full Name:</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address:</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes (optional):</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Add any additional details..."
                />
              </div>

              <div className="form-buttons">
                <button type="submit" className="confirm-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                </button>
                <button type="button" className="cancel-btn" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default BookingFormModal;